import {
    Component,
    Element,
    Event,
    EventEmitter,
    h,
    Host,
    Listen,
    Prop,
    State,
    Watch,
} from "@stencil/core";
import {
    getConfig,
    getAuthClient,
    getTrumpfUserClient,
    BaseUserModel,
} from "@trumpf/ux.utils";
import {
    I18nMessages,
    I18nTranslateFunction,
    getI18nMessage,
} from "../../utils/i18n";
import { getTranslations } from "./translations";
import { headerDialogs, headerIcons } from "./constants";

export type ViewPropType =
    | null
    | { opened: boolean }
    | { (): { opened: boolean } };

/**
 * @slot home-dialog - Content of the home dialog.
 * @slot help-dialog - Content of the help dialog.
 * @slot user-dialog - Additional info about the current user that should appear in the user dialog.
 * @slot main-menu - The application menu `uic-header-menu`.
 */
@Component({
    tag: "uic-app-header",
    styleUrl: "app-header.scss",
    shadow: false,
})
export class AppHeader {
    @Element() host: HTMLUicAppHeaderElement;

    /** The icon displayed in the home button */
    @Prop() homeIcon: string = "uic:apps";

    /** The locale used in translations */
    @Prop() locale: string = "en_US";

    /** Key-value object with translated texts */
    @Prop() translations?: I18nMessages;

    /** Information about the app user. If not set, it will be fetched from the TRUMPF User API   */
    @Prop() userData: BaseUserModel | null = null;

    /** Link to the TRUMPF user profile   */
    @Prop({ mutable: true }) userProfileUrl: string | null = null;

    /** Number that indicates new messages in the notifications badge */
    @Prop() notificationsBadge: number = 0;

    /** Flag that indicates that the notifications icon should be displayed */
    @Prop() notificationsSupported: boolean = false;

    /** The related view that should be controlled by clicking the notifications icon */
    @Prop() notificationsView: ViewPropType | null = null;

    /** Flag that indicates that the settings icon should be displayed */
    @Prop() settingsSupported: boolean = false;

    /** The related view  that should be controlled by clicking the settings icon */
    @Prop() settingsView: ViewPropType | null = null;

    /** Name of the currently opened flyout dialog */
    @State() activeDialog: string | null = null;

    /** Internal user object */
    @State() activeUserData: BaseUserModel | null = null;

    /** Translations for the current locale */
    @State() i18nResource: I18nMessages | null = null;

    /** Observer unsubscribe handlers */
    private unsubscribeUserClient: () => void | null = null;

    /** Translate function */
    private translate: I18nTranslateFunction = (key: string) => {
        return this.i18nResource !== null
            ? getI18nMessage(key, this.i18nResource)
            : key;
    };

    /** Load translations */
    @Watch("locale")
    @Watch("translations")
    loadI18n() {
        const translations = this.translations || {};
        getTranslations(this.locale || "en").then(
            (i18nResource) =>
                (this.i18nResource = {
                    ...i18nResource,
                    ...translations,
                })
        );
    }

    /** Handle userData */
    @Watch("userData")
    setUserData() {
        if (this.userData === null) {
            this.activeUserData = null;
            this.initTrumpfUserClient();
        } else {
            this.activeUserData = this.userData;
            this.removeTrumpfUserClient();
        }
    }

    /** Handle dialog opened / closed */
    @Watch("activeDialog")
    setActiveDialog() {
        this.updateSettingsView();
        this.updateNotificationsView();
    }

    /** Emitted when a dialog is closed */
    @Event({
        eventName: "uicDialogClose",
        bubbles: true,
        cancelable: false,
        composed: true,
    })
    dialogWillClose: EventEmitter<string>;

    /** Emitted when a dialog is opened */
    @Event({
        eventName: "uicDialogOpen",
        bubbles: true,
        cancelable: true,
        composed: true,
    })
    dialogWillOpen: EventEmitter<string>;

    /** Emitted when the logout button was clicked */
    @Event({
        eventName: "uicLogout",
        bubbles: true,
        cancelable: false,
        composed: true,
    })
    logoutClicked: EventEmitter<void>;

    /** Handle header-menu change events */
    @Listen("uicMenuChange")
    handleHeaderMenuChange({ detail: { name: menuName, current } }) {
        if (menuName === "dialog") {
            if (current === this.activeDialog) {
                return;
            }
            if (this.activeDialog !== null) {
                this.dialogWillClose.emit(this.activeDialog);
            }
            if (
                current !== null &&
                !this.dialogWillOpen.emit(current).defaultPrevented
            ) {
                this.activeDialog = current;
            } else {
                this.activeDialog = null;
            }
        } else if (current !== null) {
            this.activeDialog = null;
        }
    }

    /** Use TrumpfUserClient to fetch userData from API */
    private initTrumpfUserClient = async () => {
        const { websiteBaseUrl, websitePaths } = getConfig();
        this.removeTrumpfUserClient();
        const trumpfUserClient = getTrumpfUserClient();
        this.unsubscribeUserClient = trumpfUserClient.subscribe(() => {
            this.activeUserData = trumpfUserClient.getBaseUser();
        });
        await trumpfUserClient.getUserData();
        this.activeUserData = trumpfUserClient.getBaseUser();
        this.userProfileUrl = `${websiteBaseUrl}${websitePaths.userProfile}`;
    };

    private removeTrumpfUserClient = () => {
        if (this.unsubscribeUserClient !== null) {
            this.unsubscribeUserClient();
            this.unsubscribeUserClient = null;
        }
    };

    private updateSettingsView = () => {
        const view = getOpeningView(this.settingsView);
        if (view !== null) {
            view.opened = this.activeDialog === headerDialogs.SETTINGS;
        }
    };

    private updateNotificationsView = () => {
        const view = getOpeningView(this.notificationsView);
        if (view !== null) {
            view.opened = this.activeDialog === headerDialogs.NOTIFICATIONS;
        }
    };

    /** Handle dialog close button clicks */
    private handleCloseDialog = (): void => {
        if (this.activeDialog === null) return;
        this.dialogWillClose.emit(this.activeDialog);
        this.activeDialog = null;
    };

    /** Handle logout button click */
    private handleLogout = (): void => {
        this.logoutClicked.emit();
        this.handleCloseDialog();
        if (this.unsubscribeUserClient !== null) {
            this.activeUserData = null;
            getAuthClient().deleteAccessToken();
        }
    };

    /** Check if a slot content has been provided */
    private hasSlot = (name: string): boolean => {
        return this.host.querySelector(`[slot=${name}]`) !== null;
    };

    componentWillLoad() {
        this.loadI18n();
        this.setUserData();
    }

    disconnectedCallback() {
        this.removeTrumpfUserClient();
    }

    render() {
        const {
            activeDialog,
            activeUserData,
            handleCloseDialog,
            handleLogout,
            homeIcon,
            notificationsBadge,
            notificationsSupported,
            notificationsView,
            settingsSupported,
            settingsView,
            translate,
            userProfileUrl,
        } = this;
        const hasHelp = this.hasSlot("help-dialog");
        const hasLegacyNotifications =
            notificationsSupported ||
            (notificationsView !== undefined && notificationsView !== null);
        const hasNotifications = this.hasSlot("notifications-bell");
        const hasSettings =
            settingsSupported ||
            (settingsView !== undefined && settingsView !== null);
        return (
            <Host>
                <uic-header>
                    <uic-header-menu
                        slot="home"
                        name="dialog"
                        variant="secondary"
                        current={activeDialog}
                        toggle={true}
                    >
                        <uic-header-menu-item
                            name={headerDialogs.HOME}
                            icon={homeIcon}
                            icon-only={true}
                        />
                    </uic-header-menu>
                    <div slot="menu-left" onClick={handleCloseDialog}>
                        <slot name="main-menu" />
                    </div>
                    <uic-header-menu
                        slot="menu-right"
                        name="dialog"
                        current={activeDialog}
                        toggle={true}
                    >
                        {hasHelp && (
                            <uic-header-menu-item
                                icon={headerIcons.HELP}
                                name={headerDialogs.HELP}
                                label={translate("helpLabel")}
                                icon-only={true}
                            />
                        )}
                        {hasSettings && (
                            <uic-header-menu-item
                                icon={headerIcons.SETTINGS}
                                name={headerDialogs.SETTINGS}
                                label={translate("settingsLabel")}
                                icon-only={true}
                            />
                        )}
                        {hasLegacyNotifications && (
                            <uic-header-menu-item
                                icon={headerIcons.NOTIFICATIONS}
                                name={headerDialogs.NOTIFICATIONS}
                                label={translate("notificationsLabel")}
                                icon-only={true}
                                badge={notificationsBadge}
                            />
                        )}
                        {hasNotifications && (
                            <uic-header-menu-item
                                name={headerDialogs.NOTIFICATIONS}
                                label={translate("notificationsLabel")}
                                icon-only={true}
                            >
                                <slot name="notifications-bell"></slot>
                            </uic-header-menu-item>
                        )}
                        <uic-header-menu-item
                            icon={headerIcons.USER}
                            name={headerDialogs.USER}
                            label={translate("userLabel")}
                            icon-only={true}
                        />
                    </uic-header-menu>
                    <uic-flyout
                        slot="flyout"
                        position="left"
                        name={headerDialogs.HOME}
                        opened={
                            activeDialog === headerDialogs.HOME &&
                            this.hasSlot("home-dialog")
                        }
                    >
                        <div slot="content">
                            <slot name="home-dialog" />
                        </div>
                        <uic-button
                            slot="footer"
                            variant="footer-primary"
                            onClick={handleCloseDialog}
                        >
                            {translate("closeButton")}
                        </uic-button>
                    </uic-flyout>
                    <uic-flyout
                        slot="flyout"
                        position="right"
                        name={headerDialogs.HELP}
                        opened={activeDialog === headerDialogs.HELP}
                    >
                        <div slot="content">
                            <slot name="help-dialog" />
                        </div>
                        <uic-button
                            slot="footer"
                            variant="footer-primary"
                            onClick={handleCloseDialog}
                        >
                            {translate("closeButton")}
                        </uic-button>
                    </uic-flyout>
                    <uic-flyout
                        slot="flyout"
                        position="right"
                        name={headerDialogs.NOTIFICATIONS}
                        opened={activeDialog === headerDialogs.NOTIFICATIONS}
                    >
                        <div slot="content">
                            <slot name="notifications-dialog" />
                        </div>
                        <uic-button
                            slot="footer"
                            variant="footer-primary"
                            onClick={handleCloseDialog}
                        >
                            {translate("closeButton")}
                        </uic-button>
                    </uic-flyout>
                    <uic-flyout
                        slot="flyout"
                        position="right"
                        name={headerDialogs.USER}
                        opened={activeDialog === headerDialogs.USER}
                    >
                        <div slot="content">
                            <uic-app-user-info
                                noDataText={translate("noUserDataLabel")}
                                userData={activeUserData}
                                userProfileText={translate(
                                    "userProfileLinkText"
                                )}
                                userProfileUrl={userProfileUrl}
                            >
                                <slot name="user-dialog" />
                            </uic-app-user-info>
                        </div>
                        {activeUserData !== null && (
                            <uic-button
                                slot="footer"
                                variant="footer"
                                onClick={handleLogout}
                            >
                                {translate("logoutButton")}
                            </uic-button>
                        )}
                        <uic-button
                            slot="footer"
                            variant="footer-primary"
                            onClick={handleCloseDialog}
                        >
                            {translate("closeButton")}
                        </uic-button>
                    </uic-flyout>
                </uic-header>
            </Host>
        );
    }
}

const getOpeningView = (prop: ViewPropType): { opened: boolean } | null => {
    if (prop === undefined || prop === null) return null;
    const view = typeof prop === "function" ? prop() : prop;
    if (view && typeof view.opened === "boolean") {
        return view;
    }
    return null;
};
